<template>
  <div class="pie-chart" :style="{width: config.chartWidth+'px',height:config.chartHeight+'px'}">
    <div class="pie-chart-wrapper">
      <div class="pie-chart-title" :style="titleBarInlineStyle">
        {{ config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
      <img v-if="borderImgUrl" class="pie-chart-border" :src="borderImgUrl" alt=""
           :style="chartBorderTemplateImageStyle">
    </div>
    <div ref="pie" class="pie-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64, getNumberFormat } from '@/utils/common'
import { pieBaseOption } from './options'
import ChartMixin from './mixin'

export default {
  mixins: [ChartMixin],
  data () {
    return {
    }
  },
  computed: {
    style () {
      return this.config.chartSet
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
    renderData () {
      if (this.chartData?.length && Array.isArray(this.chartData)) {
        const list = []
        // 读取 第1列 & 第2列 的表头key
        const colKeyList = Object.keys(this.chartData[0]).slice(0, 2)
        // 表格数据 (去除第1行(表头))
        const tableData = this.chartData.slice(1)
        tableData.forEach(bar => {
          if ((bar[colKeyList[0]] !== '' || bar[colKeyList[0]] !== undefined) && (bar[colKeyList[1]] !== '' || bar[colKeyList[1]] !== undefined)) {
            list.push({
              name: bar[colKeyList[0]], // 第1列数据(维度)
              value: bar[colKeyList[1]], // 第2列数据(值)
            })
          }
        })
        return list.length ? list : []
      } else {
        return []
      }
    },
    // 获取列名
    categoryName () {
      const category = this.chartData?.[0].col_1 || this.chartData[0]?.y_0
      return category
    },
    // 比率配置
    settingRate () {
      const list = []
      if (this.config.chartSet.chartStyle?.setRate) {
        const colKeyList = ['col_6', 'col_7']
        const tableHeader = this.chartData[0]
        const tableData = this.chartData.slice(1)
        colKeyList.forEach(key => {
        // 排除空数据
          if (!tableData.every(row => (row[key] === '' || row[key] === undefined))) {
            list.push({
              name: tableHeader[key], // 当前数据列的表头
              data: tableData.map(bar => bar[key]), // 当前数据列
            })
          }
        })
      }
      return list
    },

  },
  watch: {
    // 全局样式监听
    resolvedChartColorsLabel () {
      this.init()
    },
    chartData: {
      handler: function (val) {
        this.init()
      },
      deep: true,
    },
    // 配置项变更
    config: {
      handler: function () {
        this.init()
      },
      deep: true,
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },

  },
  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    if (!this.config.chartSet.chartStyle?.labelContent || !this.config.chartSet.chartStyle.isLabel) {
      // 初始化 标签内容属性
      this.$set(this.config.chartSet.chartStyle, 'labelContent', 'percent')
    }
    if (!this.style.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }
    if (!this.style.chartStyle?.legendPosition) { this.$set(this.config.chartSet.chartStyle, 'legendPosition', 'top') }
    // if (!this.style.chartStyle?.isLegend) { this.$set(this.config.chartSet.chartStyle, 'isLegend', false) }
    if (!this.style.chartStyle?.legendSize) { this.$set(this.config.chartSet.chartStyle, 'legendSize', 12) }
    if (this.style.chartStyle?.sliceBorderRadius === undefined) { this.$set(this.config.chartSet.chartStyle, 'sliceBorderRadius', 0) }
    if (this.style.chartStyle?.sliceBlackFill === undefined) { this.$set(this.config.chartSet.chartStyle, 'sliceBlackFill', 0) }
  },
  mounted () {
    this.charts = echarts.init(this.$refs.pie)
    this.init()
  },
  methods: {

    async init () {
      const position = this.style.chartStyle.legendPosition
      // 半径大小
      let currentRadius = ''
      if (this.componentType === 'pie') {
        currentRadius = this.style.chartStyle.radius + '%'
      } else {
        currentRadius = [this.style.chartStyle.internalRadius + '%', this.style.chartStyle.externalRadius + '%']
      }
      // 判断label展示方式
      const content = this.config.chartSet.chartStyle?.labelContent
      const sliceBorderRadius = Number(this.style.chartStyle.sliceBorderRadius) || 0
      const sliceBlackFill = Number(this.style.chartStyle.sliceBlackFill) || 0
      const sliceItemStyle = { borderRadius: sliceBorderRadius }
      if (sliceBlackFill > 0) {
        sliceItemStyle.borderWidth = sliceBlackFill
        sliceItemStyle.borderColor = '#000000'
      }

      this.option = _.merge(_.cloneDeep(pieBaseOption), {
        tooltip: {
          textStyle: {
            fontSize: this.style.chartStyle.fontSize,
          },
          formatter: (params) => {
            const b = params.name
            const c = params.value
            const d = params.percent
            const index = params.dataIndex
            let str = `  ${c}`
            if (content !== '') {
              str = content === 'percent' ? `  ${d}%` : `  ${c}`
            }
            let rateStr = ''
            if (this.config.chartSet.chartStyle?.setRate) {
              this.settingRate.forEach(item => {
                rateStr += `<p>${item.name} ${item.data[index] || ''} </p>`
              })
            }
            return `
            <div style="font-size: 13px;">
              <div style="margin-bottom: 5px;">
                <div style="display:inline-block; width: 10px;height: 10px; vertical-align: 1px; border-radius:50px ;background-color: ${params.color};margin-right: 4px"></div> <span >${b}: ${str}</span>
              </div>
              <div >
                <span> ${this.categoryName}: ${c}</span>
                <div style="display:inline-block; vertical-align:middle; margin-left:18px; font-size:10px">
                  ${rateStr}
                </div>
              </div>
            </div>`
          },
        },
        legend: {
          show: this.style.chartStyle.isLegend,
          orient: position === 'top' ? 'horizontal' : 'vertical', // horizontal 横向; vertical 竖向
          right: position === 'top' ? 'center' : 10,
          textStyle: {
            fontSize: this.style.chartStyle.legendSize,
          },
        },
        color: COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value,
        series: [
          {
            radius: currentRadius, // 设置饼图 内外环大小
            itemStyle: sliceItemStyle,
            ...(sliceBlackFill > 0
              ? {
                emphasis: {
                  itemStyle: {
                    borderWidth: sliceBlackFill,
                    borderColor: '#000000',
                  },
                },
              }
              : {}),
            label: {
              show: this.style.chartStyle.isLabel,
              formatter: (params) => {
                const b = params.name
                const c = getNumberFormat(params.value)
                const d = params.percent || 0
                let str = ''

                if (content !== '') {
                  str = content === 'percent' ? `${b}: ${d}%` : `${b}:  ${c}`
                }
                return str
              },
              fontSize: this.style.chartStyle.fontSize,
            },
            labelLine: {
              show: this.style.chartStyle.isLabel,
            },
            data: this.renderData,
          },
        ],
      })
      this.charts.setOption(this.option)

      // 是否轮播 tooltips
      if (this.style.chartStyle.carousel && this.renderData.length) {
        this.tooltipsActive()
        this.mouseEvents()
      } else {
        if (this.timer) {
          clearInterval(this.timer)
          this.charts.dispatchAction({
            type: 'downplay',
            seriesIndex: 0,
            dataIndex: this.index,
          })
          this.index = -1
        }
      }

      // resize
      this.$nextTick(() => {
        this.charts.resize()
      })
    },
    // 提示轮播
    tooltipsActive () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      const dataLength = this.renderData.length
      // 用定时器控制高亮
      this.timer = setInterval(() => {
        // 清除之前的高亮
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.index === dataLength - 1) {
          this.index = 0
        } else {
          this.index++
        }

        // 当前下标高亮
        this.charts.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.index,
        })

        this.charts.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.index,
        })
      }, this.config.chartSet.chartStyle.carouselDelay * 1000)
    },
    // 鼠标移入移出
    mouseEvents () {
      // 鼠标划入
      this.charts.on('mouseover', () => {
        // 停止定时器，清除之前的高亮
        clearInterval(this.timer)
        this.timer = ''
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
      })
      // 鼠标划出重新定时器开始
      this.charts.on('mouseout', () => {
        this.tooltipsActive()
      })
    },
  },
}
</script>

<style lang='scss' scoped>
.pie-chart{

  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width:100%;
    overflow: visible;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;
  }
  &-title {
    padding:7px 30px;
  }
  .pie-chart-container{
    flex: 1;
  }
}
</style>
